import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '../common/Button';

const GallerySection = styled.section`
  padding: 30px 0 80px;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const GalleryItem = styled.div`
  display: ${props => props.visible ? 'block' : 'none'};
  transition: ${props => props.theme.transitions.main};
  animation: fadeIn 0.5s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const GalleryImage = styled.div`
  position: relative;
  border-radius: ${props => props.theme.borders.radius.regular};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.regular};
  
  img {
    width: 100%;
    height: 300px;
    object-fit: cover;
    transition: ${props => props.theme.transitions.main};
  }
  
  &:hover img {
    transform: scale(1.1);
  }
  
  &:hover .overlay {
    opacity: 1;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(44, 62, 80, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: ${props => props.theme.transitions.main};
  padding: 20px;
`;

const OverlayContent = styled.div`
  text-align: center;
  color: white;
`;

const ItemTitle = styled.h3`
  color: white;
  margin-bottom: 5px;
  font-size: ${props => props.theme.fontSizes.large};
`;

const ItemCategory = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 15px;
  font-size: ${props => props.theme.fontSizes.regular};
`;

const ViewMoreLink = styled.a`
  display: inline-block;
  padding: 8px 15px;
  background-color: ${props => props.theme.colors.primaryColor};
  color: white;
  border-radius: ${props => props.theme.borders.radius.small};
  font-weight: ${props => props.theme.fontWeights.semiBold};
  transition: ${props => props.theme.transitions.main};
  
  &:hover {
    background-color: white;
    color: ${props => props.theme.colors.primaryColor};
  }
`;

const LoadMoreContainer = styled.div`
  text-align: center;
  margin-top: 50px;
`;

const PortfolioGallery = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [visibleItems, setVisibleItems] = useState(6);
  
  const portfolioItems = [
    {
      id: 1,
      title: 'Project Title',
      category: 'Structural Design Project',
      type: 'projects',
      image: '/placeholder/400/300'
    },
    {
      id: 2,
      title: 'Research Title',
      category: 'Sustainable Construction Techniques',
      type: 'research',
      image: '/placeholder/400/300'
    },
    {
      id: 3,
      title: 'Best Training Institute',
      category: 'Awarded by Civil Engineering Association, 2024',
      type: 'awards',
      image: '/placeholder/400/300'
    },
    {
      id: 4,
      title: 'Excellence in Research',
      category: 'Recognized by Structural Engineering Society, 2023',
      type: 'awards',
      image: '/placeholder/400/300'
    },
    {
      id: 5,
      title: 'Project Title',
      category: 'Bridge Design Analysis',
      type: 'projects',
      image: '/placeholder/400/300'
    },
    {
      id: 6,
      title: 'Project Title',
      category: 'Building Renovation',
      type: 'projects',
      image: '/placeholder/400/300'
    },
    {
      id: 7,
      title: 'Student Name',
      category: 'TNPSC Junior Draughting Officer, 2024',
      type: 'success-stories',
      image: '/placeholder/400/300'
    },
    {
      id: 8,
      title: 'Student Name',
      category: 'Overseer, PWD, 2024',
      type: 'success-stories',
      image: '/placeholder/400/300'
    },
    {
      id: 9,
      title: 'Student Name',
      category: 'Assistant Engineer, Highways Department, 2023',
      type: 'success-stories',
      image: '/placeholder/400/300'
    },
    {
      id: 10,
      title: 'Research Title',
      category: 'Innovative Building Materials',
      type: 'research',
      image: '/placeholder/400/300'
    },
    {
      id: 11,
      title: 'Research Title',
      category: 'Earthquake Resistant Structures',
      type: 'research',
      image: '/placeholder/400/300'
    }
  ];
  
  // Listen for filter button clicks
  useEffect(() => {
    const filterButtons = document.querySelectorAll('[data-filter]');
    
    const handleFilterClick = (e) => {
      const filter = e.currentTarget.getAttribute('data-filter');
      setActiveFilter(filter);
      setVisibleItems(6); // Reset visible items when filter changes
    };
    
    filterButtons.forEach(button => {
      button.addEventListener('click', handleFilterClick);
    });
    
    return () => {
      filterButtons.forEach(button => {
        button.removeEventListener('click', handleFilterClick);
      });
    };
  }, []);
  
  const handleLoadMore = () => {
    setVisibleItems(prevVisibleItems => prevVisibleItems + 3);
  };
  
  const filteredItems = activeFilter === 'all' 
    ? portfolioItems 
    : portfolioItems.filter(item => item.type === activeFilter);
  
  const hasMoreItems = visibleItems < filteredItems.length;
  
  return (
    <GallerySection>
      <div className="container">
        <GalleryGrid>
          {filteredItems.map((item, index) => (
            <GalleryItem 
              key={item.id} 
              visible={index < visibleItems}
            >
              <GalleryImage>
                <img src={item.image} alt={item.title} />
                <Overlay className="overlay">
                  <OverlayContent>
                    <ItemTitle>{item.title}</ItemTitle>
                    <ItemCategory>{item.category}</ItemCategory>
                    <ViewMoreLink href="#">View Details</ViewMoreLink>
                  </OverlayContent>
                </Overlay>
              </GalleryImage>
            </GalleryItem>
          ))}
        </GalleryGrid>
        
        {hasMoreItems && (
          <LoadMoreContainer>
            <Button variant="secondary" onClick={handleLoadMore}>
              Load More
            </Button>
          </LoadMoreContainer>
        )}
      </div>
    </GallerySection>
  );
};

export default PortfolioGallery;
